import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Pencil, HelpCircle } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, SectionTitle,
} from "../../components/common/Brutal";

const empty = { question: "", answer: "", order: 0 };

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/faqs");
    setFaqs(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question || !form.answer) return toast.error("Question and answer required");
    const payload = { ...form, order: parseInt(form.order || 0, 10) };
    if (editing) await api.put(`/faqs/${editing}`, payload);
    else await api.post("/faqs", payload);
    toast.success(editing ? "FAQ updated" : "FAQ added");
    setForm(empty); setEditing(null); load();
  };
  const edit = (f) => { setEditing(f.id); setForm({ question: f.question, answer: f.answer, order: f.order || 0 }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Delete FAQ?")) return; await api.delete(`/faqs/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="admin-faqs">
      <SectionTitle kicker="FAQ List" title="Manage public-facing frequently asked questions" />

      <BrutalCard className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="h-5 w-5 text-[#E5247C]" />
          <h3 className="display text-2xl">{editing ? "Edit FAQ" : "Add new FAQ"}</h3>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-6 gap-3" data-testid="faq-form">
          <div className="sm:col-span-5"><BrutalLabel>Question *</BrutalLabel><BrutalInput value={form.question} onChange={(e) => setForm({...form, question: e.target.value})} data-testid="faq-question" /></div>
          <div className="sm:col-span-1"><BrutalLabel>Order</BrutalLabel><BrutalInput type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} data-testid="faq-order" /></div>
          <div className="sm:col-span-6">
            <BrutalLabel>Answer *</BrutalLabel>
            <textarea
              className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 text-base font-medium min-h-[120px]"
              value={form.answer}
              onChange={(e) => setForm({...form, answer: e.target.value})}
              data-testid="faq-answer"
            />
          </div>
          <div className="sm:col-span-6 flex gap-2">
            <BrutalButton variant="primary" type="submit" data-testid="faq-save">
              {editing ? <><Save className="h-4 w-4" /> Update FAQ</> : <><Plus className="h-4 w-4" /> Add FAQ</>}
            </BrutalButton>
            {editing && <BrutalButton variant="outline" type="button" onClick={() => { setEditing(null); setForm(empty); }}>Cancel</BrutalButton>}
          </div>
        </form>
      </BrutalCard>

      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-3">All FAQs ({faqs.length})</h3>
        <div className="space-y-2" data-testid="faq-admin-list">
          {faqs.length === 0 && <div className="text-sm text-[#6B4E9E]">No FAQs yet.</div>}
          {faqs.map((f) => (
            <div key={f.id} className="kf-border bg-white p-3 flex items-start justify-between gap-3" data-testid={`faq-row-${f.id}`}>
              <div className="min-w-0">
                <div className="font-bold text-[#3D1E6D]">{f.question}</div>
                <div className="text-sm text-[#6B4E9E] mt-1 whitespace-pre-line">{f.answer}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-1">Order: {f.order}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => edit(f)} className="kf-border bg-[#FFB400] p-1.5" data-testid={`faq-edit-${f.id}`}><Pencil className="h-3 w-3" /></button>
                <button onClick={() => del(f.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`faq-del-${f.id}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </BrutalCard>
    </div>
  );
}
